import { useState, useEffect, useRef, type ChangeEvent, type KeyboardEvent } from 'react'
import {
  fetchPlaceSuggestions,
  getPlaceLocationDetails,
  type PlaceSuggestion,
} from '../services/googleMapsPlaces'
import type { LocationGeoData } from '../utils/salzburgLocation'

export interface HeroLocationInputProps {
  label: 'FROM' | 'TO'
  placeholder: string
  value: LocationGeoData | null
  onChange: (location: LocationGeoData | null) => void
  isRestrictedToSalzburg?: boolean
  className?: string
  idPrefix?: string
}

export default function HeroLocationInput({
  label,
  placeholder,
  value,
  onChange,
  isRestrictedToSalzburg = false,
  className = '',
  idPrefix = 'hero-loc',
}: HeroLocationInputProps) {
  const [query, setQuery] = useState('')
  const [isInputActive, setIsInputActive] = useState(false)
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState<number>(-1)

  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)

  // Typography & design tokens
  const labelClass =
    'font-poppins text-[11px] font-medium uppercase leading-none tracking-[0.04em] text-[#9AA3B7] md:text-[clamp(8px,0.85vw,12px)] md:normal-case md:tracking-[-0.03em] md:text-black mb-1'

  const valueClass =
    'font-poppins text-[14px] font-bold leading-[1.15] tracking-[-0.03em] text-black md:text-[clamp(14px,1.15vw,17px)] truncate'

  const subClass =
    'font-poppins text-[12px] font-normal leading-[1.25] tracking-[-0.02em] text-[#71717A] md:text-[clamp(9px,0.75vw,11px)] md:font-medium md:tracking-[-0.03em] md:text-[#52525B] truncate mt-0.5'

  const placeholderValueClass =
    'font-poppins text-[14px] font-normal leading-[1.15] tracking-[-0.035em] text-[#9CA3AF] md:text-[clamp(14px,1.15vw,17px)]'

  // Keep input text in sync when value changes from outside
  useEffect(() => {
    if (!isInputActive) {
      setQuery(value ? value.title : '')
    }
  }, [value, isInputActive])

  // Fetch suggestions when query changes (only when user types >= 2 characters)
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    const trimmed = query.trim()
    if (!isInputActive || trimmed.length < 2) {
      setSuggestions([])
      setIsLoading(false)
      setSelectedIndex(-1)
      return
    }

    setIsLoading(true)
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const results = await fetchPlaceSuggestions(trimmed, {
          restrictToSalzburg: Boolean(isRestrictedToSalzburg),
        })
        setSuggestions(results)
      } catch (err) {
        console.error('Failed to fetch suggestions:', err)
        setSuggestions([])
      } finally {
        setIsLoading(false)
      }
    }, 280)

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [query, isInputActive, isRestrictedToSalzburg])

  // Handle outside click to close suggestions
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsInputActive(false)
        setSuggestions([])
        // Revert query to selected value title if any
        if (value) {
          setQuery(value.title)
        } else {
          setQuery('')
        }
      }
    }

    document.addEventListener('mousedown', handleOutsideClick)
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [value])

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value)
    setIsInputActive(true)
    setSelectedIndex(-1)
  }

  const handleSelectSuggestion = async (suggestion: PlaceSuggestion) => {
    setIsLoading(true)
    try {
      const fullLocation = await getPlaceLocationDetails(suggestion)
      onChange(fullLocation)
      setQuery(fullLocation.title)
      setSuggestions([])
      setIsInputActive(false)
    } catch {
      onChange({
        title: suggestion.title,
        address: suggestion.address,
        isSalzburg: suggestion.isSalzburg,
        placeId: suggestion.placeId,
      })
      setQuery(suggestion.title)
      setSuggestions([])
      setIsInputActive(false)
    } finally {
      setIsLoading(false)
    }
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange(null)
    setQuery('')
    setSuggestions([])
    setIsInputActive(false)
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setIsInputActive(false)
      setSuggestions([])
      if (value) setQuery(value.title)
      return
    }

    if (suggestions.length === 0) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
        handleSelectSuggestion(suggestions[selectedIndex])
      }
    }
  }

  const getIconElement = (type?: 'airport' | 'station' | 'hotel' | 'pin') => {
    switch (type) {
      case 'airport':
        return (
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-600">
            <svg
              className="h-3.5 w-3.5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z" />
            </svg>
          </span>
        )
      case 'station':
        return (
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-50 text-amber-600">
            <svg
              className="h-3.5 w-3.5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect width="16" height="16" x="4" y="3" rx="2" />
              <path d="M4 11h16" />
              <path d="M12 3v8" />
              <path d="m8 19-2 3" />
              <path d="m18 22-2-3" />
              <circle cx="8" cy="15" r="1" />
              <circle cx="16" cy="15" r="1" />
            </svg>
          </span>
        )
      case 'hotel':
        return (
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-purple-50 text-purple-600">
            <svg
              className="h-3.5 w-3.5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M18 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2Z" />
              <path d="M9 22v-4h6v4" />
              <path d="M8 6h.01" />
              <path d="M16 6h.01" />
              <path d="M12 6h.01" />
              <path d="M12 10h.01" />
              <path d="M12 14h.01" />
              <path d="M16 10h.01" />
              <path d="M16 14h.01" />
              <path d="M8 10h.01" />
              <path d="M8 14h.01" />
            </svg>
          </span>
        )
      default:
        return (
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500">
            <svg
              className="h-3.5 w-3.5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
          </span>
        )
    }
  }

  const isDropdownVisible = isInputActive && query.trim().length >= 2

  return (
    <div
      ref={containerRef}
      className={`relative flex h-full w-full flex-col justify-center text-left ${className}`}
    >
      {/* Label (clean without intrusive badges) */}
      <p className={labelClass}>{label}</p>

      {/* Input / Value Container */}
      <div
        className="group relative flex w-full items-center cursor-text min-w-0"
        onClick={() => {
          setIsInputActive(true)
          if (inputRef.current) {
            inputRef.current.focus()
            if (value && query === value.title) {
              inputRef.current.select()
            }
          }
        }}
      >
        {/* If a location is selected and input is not active, display title + full subtitle */}
        {value && !isInputActive ? (
          <div className="flex min-w-0 flex-1 flex-col pr-7">
            <p className={valueClass}>{value.title}</p>
            <p className={subClass}>{value.address}</p>
          </div>
        ) : (
          <input
            ref={inputRef}
            id={`${idPrefix}-${label.toLowerCase()}-input`}
            type="text"
            value={query}
            onChange={handleInputChange}
            onFocus={() => {
              setIsInputActive(true)
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            autoComplete="off"
            className={`w-full bg-transparent pr-7 font-poppins outline-none focus:ring-0 ${
              query ? valueClass : placeholderValueClass
            }`}
          />
        )}

        {/* Clear (✕) or Loading indicator */}
        <div className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center">
          {isLoading ? (
            <div
              className="h-4 w-4 animate-spin rounded-full border-2 border-brand-orange border-t-transparent"
              title="Searching Google Maps..."
            />
          ) : (value || query) ? (
            <button
              type="button"
              onClick={handleClear}
              className="flex h-5 w-5 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-black transition-colors"
              title="Clear location"
              aria-label={`Clear ${label} location`}
            >
              ✕
            </button>
          ) : null}
        </div>
      </div>

      {/* Google Maps Places Dropdown - ONLY appears when user types */}
      {isDropdownVisible && (
        <div
          role="listbox"
          aria-label={`${label} location suggestions`}
          className="
            absolute left-0 top-[calc(100%+10px)] z-[110]
            max-h-[320px] w-full min-w-[320px] sm:min-w-[420px] md:min-w-[460px]
            overflow-y-auto rounded-[14px] bg-white
            p-2 shadow-[0_14px_35px_rgba(0,0,0,0.18)]
            border border-[#E5E9EB]
          "
        >
          {suggestions.length > 0 ? (
            suggestions.map((suggestion, idx) => {
              const isSelected = idx === selectedIndex

              return (
                <button
                  key={suggestion.placeId || `${suggestion.title}-${idx}`}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onMouseEnter={() => setSelectedIndex(idx)}
                  onClick={() => handleSelectSuggestion(suggestion)}
                  className={`
                    flex w-full items-start gap-3 rounded-[10px] px-3 py-2.5 text-left transition-colors
                    ${isSelected ? 'bg-[#F2F4F7]' : 'hover:bg-[#F9FAFB]'}
                  `}
                >
                  <div className="mt-0.5">
                    {getIconElement(suggestion.iconType)}
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className="font-poppins text-[14px] font-semibold leading-snug text-black truncate">
                      {suggestion.title}
                    </p>
                    <p className="mt-0.5 font-poppins text-[12px] leading-tight text-[#6B7280] line-clamp-1">
                      {suggestion.address}
                    </p>
                  </div>
                </button>
              )
            })
          ) : !isLoading ? (
            <div className="py-4 text-center font-poppins text-[13px] text-[#71717A]">
              No locations found
            </div>
          ) : null}

          {/* Clean footer attribution */}
          <div className="mt-1 flex items-center justify-between border-t border-[#F0F2F5] px-2 pt-1.5 font-poppins text-[10px] text-[#9CA3AF]">
            <span>Google Maps Places</span>
            <span>Private Transfers</span>
          </div>
        </div>
      )}
    </div>
  )
}
